/**
 * AgentChart — agent-styled wrapper around the shared `ChartFromSpec`
 * primitive.
 *
 * The rendering itself (line/bar/area/pie + axis inference + colours +
 * y-axis formatters) lives in `charts/ChartFromSpec.js` so the AI
 * agent's charts share styling with the bespoke dashboard charts.
 * THIS file only owns the agent-specific chrome (gradient-tinted
 * callout box, title, description).
 *
 * Spec format — see charts/ChartFromSpec.js for the wire schema. The
 * agent emits it as a fenced ```chart code block:
 *
 *   ```chart
 *   {"type":"line", ...}
 *   ```
 *
 * PortfolioAgent.js intercepts those blocks via ReactMarkdown's
 * `components.code` slot and renders this component.
 */

import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';

import ChartFromSpec from './charts/ChartFromSpec';


export default function AgentChart({ spec }) {
  // We parse here too so we can pick title/description out for the
  // callout chrome. `ChartFromSpec` will parse again and use the same
  // body — the cost of the double-parse is negligible.
  const parsed = useMemo(() => {
    try {
      return typeof spec === 'string' ? JSON.parse(spec) : spec;
    } catch {
      return null;
    }
  }, [spec]);

  const title       = parsed?.title;
  const description = parsed?.description;

  return (
    <Box sx={{
      my: 1.5,
      px: 1.5, py: 1.25,
      borderRadius: 1.5,
      border: '1px solid', borderColor: 'divider',
      // Subtle agent-brand tint so chart blocks read distinctly from
      // bespoke dashboard charts even though they share rendering.
      bgcolor: 'rgba(217, 138, 43, 0.03)',
    }}>
      {title && (
        <Typography sx={{
          fontSize: '0.8rem', fontWeight: 700, color: 'text.primary',
          letterSpacing: '-0.005em', mb: description ? 0.25 : 0.75,
        }}>
          {title}
        </Typography>
      )}
      {description && (
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 0.75 }}>
          {description}
        </Typography>
      )}
      <ChartFromSpec spec={spec} />
    </Box>
  );
}
